import { useEffect } from 'react'

import { useMemoizedFn } from 'ahooks'
import { Flex, Tag } from 'antd'

import { useFeedback } from '@eastcoal/common/hooks/useFeedback'
import { pick } from '@eastcoal/common/utils/object'

import { $getNodeByKey } from '@eastcoal/editor/exports'
import { useEditor } from '@eastcoal/editor/hooks/useEditor'

import {
  $isTemplateVariableNode,
  type TemplateVariableNode
} from '../../../utils/templateVariable'
import { useContractDesignerStore } from '../../Store/hooks/useStore'

const useRemoveNotExistedVariableFn = () => {
  const editor = useEditor()
  const { modal } = useFeedback()
  const { usedNodeKeys, hasTemplateVariable, removeUsedNodeKeys } =
    useContractDesignerStore(state =>
      pick(state, ['usedNodeKeys', 'hasTemplateVariable', 'removeUsedNodeKeys'])
    )

  return useMemoizedFn(() => {
    const notExistedNodes: TemplateVariableNode[] = []
    const notExistedNodeNames: Set<string> = new Set()
    editor.getEditorState().read(() => {
      for (const nodeKey of usedNodeKeys) {
        const node = $getNodeByKey(nodeKey)
        if ($isTemplateVariableNode(node)) {
          const variable = node.getVariable()
          if (!hasTemplateVariable(variable.id)) {
            notExistedNodes.push(node)
            if (variable.name) {
              notExistedNodeNames.add(variable.name)
            }
          }
        }
      }
    })
    if (notExistedNodes.length === 0) {
      return
    }
    modal.warning({
      title: '存在未定义的模板变量',
      content: (
        <div>
          <span>以下变量已不存在，将从模板中自动移除：</span>
          <Flex gap={8} justify='start' wrap='wrap'>
            {Array.from(notExistedNodeNames).map(name => (
              <Tag key={name} color='red'>
                {name}
              </Tag>
            ))}
          </Flex>
        </div>
      ),
      onOk() {
        editor.update(() => {
          notExistedNodes.forEach(node => node.remove())
        })
        removeUsedNodeKeys(...notExistedNodes.map(node => node.getKey()))
      }
    })
  })
}

export const useRemoveNotExistedVariable = () => {
  const removeNotExistedVariable = useRemoveNotExistedVariableFn()
  const { formData, templateVariableData } = useContractDesignerStore(state =>
    pick(state, ['formData', 'templateVariableData'])
  )
  useEffect(() => {
    let timer: number | undefined
    if (formData?.jsonContent && templateVariableData?.length) {
      // 给一点时间，等到编辑器渲染完成
      window.setTimeout(removeNotExistedVariable, 3000)
    }
    return () => {
      if (timer) {
        clearTimeout(timer)
      }
    }
  }, [removeNotExistedVariable, formData, templateVariableData])
}
